import { Button, TextField } from "@shopify/polaris"
import {
  ChevronDownIcon,
  ChevronRightIcon,
  DragHandleIcon,
} from "@shopify/polaris-icons"
import classNames from "classnames"
import React, { forwardRef, HTMLAttributes, useState } from "react"

import styles from "./TreeItem.module.scss"

export interface Props extends Omit<HTMLAttributes<HTMLLIElement>, "id"> {
  childCount?: number
  clone?: boolean
  collapsed?: boolean
  depth: number
  disableInteraction?: boolean
  disableSelection?: boolean
  ghost?: boolean
  handleProps?: any
  indicator?: boolean
  indentationWidth: number
  value: string
  onCollapse?(): void
  onRemove?(): void
  wrapperRef?(node: HTMLLIElement): void
  descendantsCount?: number
  isLastChild?: boolean
  parentDepth?: number
  disableDrag?: boolean
  isSpecial?: boolean
  url?: string
  onLabelChange?: (value: string) => void
  onUrlChange?: (value: string) => void
  specialChildrenCount?: number
  trackOrderPlaceholder?: string
}

export const TreeItem = forwardRef<HTMLDivElement, Props>(
  (
    {
      childCount,
      clone,
      depth,
      disableSelection,
      disableInteraction,
      ghost,
      handleProps,
      indentationWidth,
      indicator,
      collapsed,
      onCollapse,
      onRemove,
      style,
      value,
      title,
      wrapperRef,
      descendantsCount = 0,
      isLastChild = false,
      parentDepth = depth > 0 ? depth - 1 : 0,
      disableDrag,
      isSpecial,
      url,
      onLabelChange,
      onUrlChange,
      specialChildrenCount,
      trackOrderPlaceholder,
      ...props
    },
    ref,
  ) => {
    const [localLabel, setLocalLabel] = useState(title || "")
    const [localUrl, setLocalUrl] = useState(url || "")

    const handleToggle = () => {
      if (onCollapse) {
        onCollapse()
      }
    }

    const handleLabelChange = (newValue: string) => {
      // 限制255字符，超出则裁剪
      const trimmedValue =
        newValue.length > 255 ? newValue.slice(0, 255) : newValue

      setLocalLabel(trimmedValue)
      if (onLabelChange) {
        onLabelChange(trimmedValue)
      }
    }

    const handleUrlChange = (newValue: string) => {
      setLocalUrl(newValue)
      if (onUrlChange) {
        onUrlChange(newValue)
      }
    }

    // 计算精确的子节点总高度
    const calculateChildrenHeight = () => {
      if (descendantsCount > 0) {
        // 基础高度：所有子节点按普通高度计算
        const baseHeight = (44 + 4) * descendantsCount
        // 特殊子节点的额外高度：(80+4) - (44+4) = 36px
        const extraHeight = (specialChildrenCount || 0) * 36

        return baseHeight + extraHeight
      }
      return 0
    }

    const verticalLineHeight = calculateChildrenHeight()

    return (
      <li
        className={classNames(
          styles.Wrapper,
          collapsed ? styles.hiddenAfter : null,
          clone && styles.clone,
          ghost && styles.ghost,
          indicator && styles.indicator,
          disableSelection && styles.disableSelection,
          disableInteraction && styles.disableInteraction,
          isSpecial && styles.SpecialItem,
        )}
        ref={wrapperRef}
        data-special={isSpecial ? "true" : "false"}
        style={
          {
            "--spacing": `${indentationWidth * depth}px`,
            "--indent-offset": `${indentationWidth / 2}px`,
            "--vertical-padding": "11px",
            "--descendants-count": descendantsCount,
            "--is-last-child": isLastChild ? 1 : 0,
            "--parent-spacing": `${indentationWidth * parentDepth}px`,
            "--vertical-line-height": `${verticalLineHeight}px`,
          } as React.CSSProperties
        }
        {...props}
      >
        <div className={styles.TreeItem} ref={ref} style={style}>
          <div
            className={classNames(
              styles.Handle,
              disableDrag && styles.HandleDisabled,
            )}
            {...handleProps}
          >
            <Button
              icon={DragHandleIcon}
              variant="plain"
              accessibilityLabel={`Move ${value}`}
              disabled={disableDrag}
            />
          </div>

          {onCollapse && (
            <div
              className={`${styles.Chevron} ${collapsed ? styles.isCollapsed : ""}`}
            >
              <Button
                icon={!collapsed ? ChevronDownIcon : ChevronRightIcon}
                variant="monochromePlain"
                onClick={handleToggle}
                accessibilityLabel={`${!collapsed ? "Collapse" : "Expand"} ${title}`}
              />
            </div>
          )}

          {isSpecial ? (
            <div className={styles.SpecialContent}>
              <div className={styles.InputGroup}>
                <div className={styles.InputWrapper}>
                  <TextField
                    value={localLabel}
                    onChange={handleLabelChange}
                    placeholder={trackOrderPlaceholder || "Track order"}
                    autoComplete="off"
                    label="Label"
                  />
                </div>
                <div className={styles.InputWrapper}>
                  <TextField
                    readOnly
                    value={localUrl}
                    onChange={handleUrlChange}
                    placeholder="Search or paste link"
                    autoComplete="off"
                    label="Link"
                  />
                </div>
              </div>
            </div>
          ) : (
            <span className={styles.Text}>{title}</span>
          )}

          {clone && childCount && childCount > 1 ? (
            <span className={styles.Count}>{childCount}</span>
          ) : null}
        </div>
      </li>
    )
  },
)
